if (!customElements.get('tfg-review-carousel')) {
  class TfgReviewCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.currentIndex = 0;
      this.scrollTimer = null;
      this.autoplayTimer = null;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(Number(this.dataset.autoplayDelay) || 6000, 3000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.viewport || this.slides.length < 2) {
        this.updateState();
        return;
      }

      this.previousButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1, true));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1, true));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index, true)));

      this.viewport.addEventListener('scroll', () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.syncIndexFromScroll(), 80);
      }, { passive: true });

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      this.addEventListener('pointerdown', () => this.pauseAfterInteraction(), { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.reducedMotion.addEventListener?.('change', this.handleMotionPreference);

      this.updateState();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.clearTimeout(this.scrollTimer);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.reducedMotion?.removeEventListener?.('change', this.handleMotionPreference);
    }

    handleVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };

    handleMotionPreference = () => {
      if (this.reducedMotion.matches) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };

    syncIndexFromScroll() {
      const viewportWidth = this.viewport.clientWidth || 1;
      const nextIndex = Math.round(this.viewport.scrollLeft / viewportWidth);
      this.currentIndex = Math.min(Math.max(nextIndex, 0), this.slides.length - 1);
      this.updateState();
    }

    goTo(index, userInitiated = false) {
      if (!this.slides.length) return;

      const normalizedIndex = (index + this.slides.length) % this.slides.length;
      this.currentIndex = normalizedIndex;
      this.viewport.scrollTo({
        left: this.slides[normalizedIndex].offsetLeft,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.updateState();

      if (userInitiated) {
        this.pauseAfterInteraction();
      }
    }

    updateState() {
      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', index === this.currentIndex ? 'false' : 'true');
      });
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
      });
    }

    startAutoplay() {
      this.stopAutoplay();
      if (
        !this.autoplayEnabled ||
        this.slides.length < 2 ||
        this.reducedMotion.matches ||
        document.hidden ||
        this.matches(':hover') ||
        this.contains(document.activeElement)
      ) return;

      this.autoplayTimer = window.setInterval(() => this.goTo(this.currentIndex + 1), this.autoplayDelay);
    }

    pauseAfterInteraction() {
      this.autoplayEnabled = false;
      this.viewport?.setAttribute('aria-live', 'polite');
      this.stopAutoplay();
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('tfg-review-carousel', TfgReviewCarousel);
}
